import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { Toaster } from 'sonner';
import './App.css';
import { LangProvider, useLang } from './i18n';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Catalog from './pages/Catalog';
import CategoryDetail from './pages/CategoryDetail';
import WorldDetail from './pages/WorldDetail';
import Collections from './pages/Collections';
import CollectionDetail from './pages/CollectionDetail';
import Product from './pages/Product';
import WhereToBuy from './pages/WhereToBuy';
import Company from './pages/Company';
import { Magazine, Article } from './pages/Magazine';
import Stories from './pages/Stories';
import { Reseller, Horeca } from './pages/Reseller';
import Contact from './pages/Contact';
import PressArea from './pages/PressArea';
import Privacy from './pages/Privacy';
import Accessibilita from './pages/Accessibilita';
import NoteLegali from './pages/NoteLegali';
import CookiePolicy from './pages/CookiePolicy';
import Unsubscribe from './pages/Unsubscribe';
import { Shades, ShadeDetail } from './pages/Shades';
import { TableBuilder, TableShared } from './pages/TableBuilder';
import Season from './pages/Season';
import { BackToTop } from './components/BackToTop';
import { PageTransition } from './components/PageTransition';
import { ScrollRestoration } from './components/ScrollRestoration';

const Victionary = lazy(() => import('./pages/Victionary'));

const Shell = ({ children }) => {
  const { t } = useLang();
  return (
    <div className="grana min-h-[100dvh]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[var(--z-modal)] focus:bg-inchiostro focus:px-4 focus:py-3 focus:text-[14px] focus:font-semibold focus:text-carta"
      >
        {t('nav.skipToContent')}
      </a>
      <Header />
      <main id="main">
        <PageTransition>{children}</PageTransition>
      </main>
      <Footer />
    </div>
  );
};

const page = (node) => <Shell>{node}</Shell>;

// Il vecchio sito viveva sotto /it/ e Google lo ha ancora indicizzato:
// /it/qualcosa porta a /qualcosa.
const LegacyItPrefix = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/it\/?/, '/')}${search}${hash}`} replace />;
};

// /en e /en/qualcosa (link esterni, vecchio sito): l'inglese vive su ?lang=en,
// lo stesso indirizzo dichiarato in hreflang e sitemap.
const EnPrefix = () => {
  const { pathname, search, hash } = useLocation();
  const q = new URLSearchParams(search);
  q.set('lang', 'en');
  return <Navigate to={`${pathname.replace(/^\/en\/?/, '/')}?${q}${hash}`} replace />;
};

function App() {
  return (
    // ErrorBoundary il piu' esterno possibile: cosi' intercetta anche gli
    // errori dei provider (lingua, router, smooth scroll), non solo quelli
    // delle pagine.
    <ErrorBoundary>
      <MotionConfig reducedMotion="user">
        <LangProvider>
          <BrowserRouter>
            <ScrollRestoration />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={page(<Home />)} />
                {/* Una pagina sola per tutte le stagioni: il contenuto arriva dal
                    registro. Il vecchio indirizzo del Natale resta valido. */}
                <Route path="/stagioni/:slug" element={page(<Season />)} />
                <Route path="/stagioni" element={<Navigate to="/stagioni/natale" replace />} />
                <Route path="/storie/natale" element={<Navigate to="/stagioni/natale" replace />} />
                <Route path="/catalogo" element={page(<Catalog />)} />
                <Route path="/catalogo/mondo/:slug" element={page(<WorldDetail />)} />
                <Route path="/catalogo/:slug" element={page(<CategoryDetail />)} />
                <Route path="/collezioni" element={page(<Collections />)} />
                <Route path="/storie/victionary" element={<Victionary />} />
                <Route path="/collezioni/:slug" element={page(<CollectionDetail />)} />
                <Route path="/prodotti/:slug" element={page(<Product />)} />
                <Route path="/storie/shades" element={page(<Shades />)} />
                <Route path="/storie/shades/:slug" element={page(<ShadeDetail />)} />
                <Route path="/componi" element={page(<TableBuilder />)} />
                <Route path="/tavola/:code" element={page(<TableShared />)} />
                <Route path="/dove-comprare" element={page(<WhereToBuy />)} />
                <Route path="/azienda" element={page(<Company />)} />
                <Route path="/storie" element={page(<Stories />)} />
                <Route path="/storie/articoli" element={page(<Magazine />)} />
                <Route path="/storie/articoli/:slug" element={page(<Article />)} />
                <Route path="/professionisti/rivenditori" element={page(<Reseller />)} />
                <Route path="/professionisti/horeca" element={page(<Horeca />)} />
                <Route path="/contatti" element={page(<Contact />)} />
                <Route path="/area-stampa" element={page(<PressArea />)} />
                <Route path="/informativa-privacy" element={page(<Privacy />)} />
                <Route path="/accessibilita" element={page(<Accessibilita />)} />
                <Route path="/note-legali" element={page(<NoteLegali />)} />
                <Route path="/cookie-policy" element={page(<CookiePolicy />)} />
                <Route path="/disiscriviti" element={page(<Unsubscribe />)} />
                <Route path="/it/*" element={<LegacyItPrefix />} />
                <Route path="/en/*" element={<EnPrefix />} />
                <Route path="*" element={page(<NotFound />)} />
              </Routes>
            </Suspense>
            <BackToTop />
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  background: '#221D1A',
                  color: '#F7F4EE',
                  border: 'none',
                  borderRadius: 0,
                  fontFamily: 'Archivo, sans-serif',
                  fontSize: '13px',
                },
              }}
            />
          </BrowserRouter>
          </LangProvider>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;
